/* istanbul ignore file */
import {
  flatten,
  forEach,
  includes,
  isEmpty,
  isNull,
  inRange,
  parseInt,
  replace,
  split,
  toLower,
  has,
  get,
  without,
  filter,
  keys,
} from "lodash";
import moment from "moment";
import settings from "../../../../../settings";

import {
  constructAdminAPIEndPoint,
  cursorPaginationApi,
  fetchHandler,
  reduceRatio,
  commaSeparatedStringToArray,
  splitCommaSeparatedStringToArray,
  fixedEncodeURIComponent,
} from "../../../../../utils";
import { GetGeo } from "../../../context/GetGeo/index";
import ErrorMessages from "../../../DisplayError/ErrorMessages";

const validate = require("uuid-validate");

export let venueList = [];
export let countryList = [];

// Return error object to importer
export const errorData = (errorMessage) => [
  {
    message: errorMessage,
    level: "error",
  },
];

// Returns warning object for flatfile field
export const warningData = (warningMessage) => [
  {
    message: warningMessage,
    level: "warning",
  },
];

/* List field validations containing invalid string elements */
export const validateListFields = (listFieldValues) => {
  const fieldList = splitCommaSeparatedStringToArray(listFieldValues);
  const filteredList = fieldList.filter((element) => {
    /* Swap left/right microsoft/html quotes with standard quotes */
    element = element.replace(/”|“/g, '"');
    element = element.replace(/’|‘/g, "'");

    let regex = new RegExp(/"(\s.+)"|""|'(\s.+)'|''/g);
    let filteredQuoteElements = element.match(regex);

    return (
      isEmpty(
        element.trim()
      ) /* Filter elements containing only Whitespaces */ ||
      !element /* Filter empty elements */ ||
      !isNull(
        filteredQuoteElements
      ) /* Filter elements containing only quotes OR whitespaces within quotes */
    );
  });
  if (filteredList.length) {
    return errorData("Please enter a valid data");
  }
};

// Validate field of array / list type
export const ListFieldValidation = (record, fieldName, availableChoices) => {
  const fieldList = splitCommaSeparatedStringToArray(record[fieldName]);
  for (const field of fieldList) {
    if (!availableChoices.includes(field.trim())) {
      return errorData(
        `${field}: Invalid ${fieldName}, please enter a valid choice`
      );
    }
  }
};

// Validate value against given range
export const valueRangeValidation = (min, max, value) => {
  const errorMessage = `A value should be in between ${min} and ${max}`;
  if (!inRange(Number(value), min, max + 1)) {
    return errorData(errorMessage);
  }
};

const isValidAspectRatio = (formattedValue, reducedRatio) => {
  const isValid = /^[0-9]+:{1}[0-9]+$/.test(formattedValue);
  let errorObject;
  if (!isValid) {
    const errorMessage = `Aspect ratio should be in integers separated by single colon format. The nearest ratio is ${reducedRatio}`;
    errorObject = errorData(errorMessage);
  }
  return errorObject;
};

const calculateAspectRatio = (record, formattedValue) => {
  const aspectRatioValues = formattedValue.split(":");
  const actualAspectRatio = (
    Number(record["slot.w"]) / Number(record["slot.h"])
  ).toFixed(2);
  const aspectRatioLhs = Number(aspectRatioValues[0]);
  const aspectRatioRhs = Number(aspectRatioValues[1]);

  const calculatedAspectRatio =
    aspectRatioRhs > 0
      ? (aspectRatioLhs / aspectRatioRhs).toFixed(2)
      : aspectRatioLhs.toFixed(2);
  const tenPercentOfActualAspectRatio = actualAspectRatio / 10;
  const minErrorMargin = (
    Number(actualAspectRatio) - Number(tenPercentOfActualAspectRatio)
  ).toFixed(2);
  const maxErrorMargin = (
    Number(actualAspectRatio) + Number(tenPercentOfActualAspectRatio)
  ).toFixed(2);

  return { calculatedAspectRatio, minErrorMargin, maxErrorMargin };
};

// Cross field validation - it accept values depending on slot height and width else raise error
export const aspectRatioCrossValidation = (record, formattedValue) => {
  let errorObject;
  if (!isEmpty(record["slot.h"]) && !isEmpty(record["slot.w"])) {
    const reducedRatio = reduceRatio(
      Number(record["slot.w"]),
      Number(record["slot.h"])
    );
    const { calculatedAspectRatio, minErrorMargin, maxErrorMargin } =
      calculateAspectRatio(record, formattedValue);
    errorObject = isValidAspectRatio(formattedValue, reducedRatio);

    if (
      !(
        Number(minErrorMargin) <= Number(calculatedAspectRatio) &&
        Number(calculatedAspectRatio) <= Number(maxErrorMargin)
      )
    ) {
      const errorMessage = `${record["asset.aspect_ratio"]}: Aspect ratio should match slot width/height.
      Please provide correct values for width, height, and aspect_ratio. The nearest ratio is ${reducedRatio}`;
      errorObject =
        errorObject === undefined ? errorData(errorMessage) : errorObject;
    }
  }
  return errorObject;
};

// check whether date is future or not
export const isFutureDate = (inputDate) => {
  const dateToCompare = moment(inputDate);
  if (!isEmpty(inputDate)) {
    return !(moment().diff(dateToCompare) >= 0);
  }
};

export const isValidateDate = (dateValue) => {
  const errorMessage =
    "Please enter a valid date (supported formats : MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY, M-D-YYYY)";
  if (
    !isEmpty(dateValue) &&
    !moment(dateValue, ["M/D/YYYY", "M-D-YYYY"], true).isValid()
  ) {
    return errorData(errorMessage);
  }
};

// Cross validation between status and start_date
export const validateStartDate = (record, inputDate) => {
  if (record.status === "1" && !isEmpty(inputDate)) {
    if (!isFutureDate(inputDate.trim())) {
      return errorData(
        "start_date should be a future date when status is pending"
      );
    }
  }
  return isValidateDate(inputDate);
};

// Cross validation between min_duration and max_duration of slot.
export const validateMinMaxDuration = (record) => {
  if (
    parseFloat(record["slot.max_duration"]) <
    parseFloat(record["slot.min_duration"])
  ) {
    return errorData(
      "max_duration must be greater than 1 and equal to or greater than min_duration"
    );
  }
};

export const dateCompare = (firstDate, secondDate) => {
  return moment(firstDate).isAfter(secondDate);
};

// To perform cross field validation
export const crossFieldValidation = (
  record,
  fieldName,
  formattedValue,
  uploadType
) => {
  if (fieldName === "asset.aspect_ratio") {
    return aspectRatioCrossValidation(record, formattedValue);
  }
  if (fieldName === "start_date" && uploadType === "Ad Units") {
    return validateStartDate(record, formattedValue);
  }
  if (uploadType === "Placements") {
    return validateCrossDateFields(record, fieldName);
  }
};

export const validateCrossDateFields = (record, fieldName) => {
  if (
    fieldName === "start_date" &&
    !isEmpty(record["end_date"]) &&
    !isEmpty(record["start_date"])
  ) {
    if (dateCompare(record["start_date"], record["end_date"])) {
      return errorData("start_date can not be greater than end_date");
    }
  }

  const dateFields = ["end_date", "end_time", "start_date", "start_time"];
  const excludeField = without(dateFields, fieldName);

  const excludedFieldisNotEmpty =
    !isEmpty(get(record, excludeField[0])) ||
    !isEmpty(get(record, excludeField[1])) ||
    !isEmpty(get(record, excludeField[2]));

  if (isEmpty(get(record, fieldName, "")) && excludedFieldisNotEmpty) {
    return errorData(
      `${fieldName} can not be empty if ${excludeField[0]}, ${excludeField[1]} or ${excludeField[2]} present`
    );
  }
};

export const validateMinMaxTime = (formattedRecord) => {
  const startTimeValues = formattedRecord["dayparts.start_time"].value;
  const endTimeValues = formattedRecord["dayparts.end_time"].value;
  const startTimeFieldValues = commaSeparatedStringToArray(startTimeValues);
  const endTimeFieldValues = commaSeparatedStringToArray(endTimeValues);

  if (startTimeFieldValues.length === endTimeFieldValues.length) {
    let error = false;
    forEach(startTimeFieldValues, (startTime, index) => {
      if (
        dateCompare(
          moment(startTime, "h:mm A"),
          moment(endTimeFieldValues[index], "h:mm A")
        )
      ) {
        error = true;
      }
    });
    if (error) {
      return errorData(
        "dayparts.start_time can not be greater than dayparts.end_time"
      );
    }
  }
};

export const validateDateTimeFields = (record, formattedRecord) => {
  const startDate = get(record, "start_date", "");
  const endDate = get(record, "end_date", "");
  const startTime = formattedRecord["start_time"].value;
  const endTime = formattedRecord["end_time"].value;
  const isStartTimeGreater = dateCompare(
    moment(startTime, "h:mm A"),
    moment(endTime, "h:mm A")
  );

  if (moment(startDate).isSame(endDate) && isStartTimeGreater) {
    return errorData("start_time cannot be greater than end_time");
  }
};

export const validateDaypartFields = (record, fieldName) => {
  const weekDayValues = commaSeparatedStringToArray(
    get(record, "dayparts.weekday", "")
  );
  const startTimeFieldValues = commaSeparatedStringToArray(
    get(record, "dayparts.start_time", "")
  );
  const endTimeFieldValues = commaSeparatedStringToArray(
    get(record, "dayparts.end_time", "")
  );

  const isDaypartsFieldNotEmpty =
    !isEmpty(record["dayparts.weekday"]) &&
    !isEmpty(record["dayparts.start_time"]) &&
    !isEmpty(record["dayparts.end_time"]);
  const isDaypartsFieldEqualLength =
    weekDayValues.length === startTimeFieldValues.length &&
    weekDayValues.length === endTimeFieldValues.length;

  if (isDaypartsFieldNotEmpty) {
    if (!isDaypartsFieldEqualLength) {
      return errorData(
        "Dayparts.weekday, dayparts.start_time and dayparts.end_time all should contain same number of items"
      );
    }
    return;
  }

  const daypartsField = [
    "dayparts.weekday",
    "dayparts.start_time",
    "dayparts.end_time",
  ];
  const excludeFieldList = without(daypartsField, fieldName);

  if (
    isEmpty(get(record, fieldName, "")) &&
    (!isEmpty(get(record, excludeFieldList[0], "")) ||
      !isEmpty(get(record, excludeFieldList[1], "")))
  ) {
    return errorData(
      `${fieldName} cannot be empty if the ${excludeFieldList[0]} or ${excludeFieldList[1]} present`
    );
  }
};

/* 
Validates the "Year 2038 problem" which arises from the use of 32-bit signed integers to represent time in computer systems.
These integers count seconds since the "epoch," which is typically January 1, 1970, at 00:00:00 UTC.
The maximum value for a signed 32-bit integer (2,147,483,647) corresponds to January 19, 2038, at 03:14:07 UTC.
All our timestamp fields (like adunit.start_date and placement.end_date) are defined as int32 in pxproto.
This leads to the Snowplow ref jobs failing with error "Failed to parse end_date field: Value out of range: 3187291800"
When trying to parse timestamps greater than int32.
The function allows dates until January 18, 2038, to prevent issues related to the Year 2038 problem.
Reference links:
- https://en.wikipedia.org/wiki/Year_2038_problem 
*/
export const int32MaxDateValidation = (dateValue) => {
  const errorMessage = "Cannot be greater than 18 January 2038";
  const int32MaxDate = settings.INT32_MAX_DATE.split("T")[0];

  if (
    !isEmpty(dateValue) &&
    dateCompare(
      moment(dateValue, ["M/D/YYYY", "M-D-YYYY"]),
      moment(int32MaxDate, "YYYY-M-D")
    )
  ) {
    return errorData(errorMessage);
  }
};

export const missingColValidation = (fieldName, value, record, params) => {
  if (value === params.if_val && isEmpty(record[params.colName])) {
    return errorData(
      `${params.colName} is missing in uploaded file. It is required when ${fieldName} is set to ${value}.`
    );
  }
};

let isNetworkCalled = false;
let previousOrgId = "";
let networkIdNameList = [];
export const getNetwork = async (orgId, searchParams = []) => {
  if (!isNetworkCalled || previousOrgId !== orgId) {
    const headers = {
      "cache-control": "no-cache, no-store",
    };
    previousOrgId = orgId;
    await cursorPaginationApi(orgId, "networks", headers, searchParams).then(
      (json) => {
        isNetworkCalled = true;
        networkIdNameList = json.map((x) => {
          return { networkId: x.id, networkName: x.name };
        });
      }
    );
  }
  return networkIdNameList;
};

export const getLocationData = async (latlon, name) => {
  if (has(latlon, "location.lat")) {
    const searchParams = [];
    forEach(latlon, (val, key) => {
      searchParams.push({ key: key.split(".")[1], value: val });
    });

    searchParams.push({ key: "name", value: name });

    return await fetchHandler({
      url: constructAdminAPIEndPoint({
        url: "geolocation",
        searchParams: searchParams,
      }),
    })
      .then((json) => json)
      .catch((error) => errorData(error.data.errors[0]));
  }
};

export const getGeoTargetingData = async (targeting) => {
  let promises = [];
  let result = [];
  if (has(targeting, "targeting.geo.country")) {
    let countryList = commaSeparatedStringToArray(
      get(targeting, "targeting.geo.country")
    );
    countryList.forEach((country) => {
      promises.push(GetGeo(country).catch((error) => result));
    });
    const apiResponse = await Promise.all(promises);
    apiResponse.forEach((data) => {
      if (!isEmpty(data)) result = [...result, data[0]];
    });
    return result;
  }
};

export const isValidTime = (timeValue) => {
  if (!isEmpty(timeValue)) {
    const timeValidationRegex = /^(0?[1-9]|1[012])(:[0-5][0-9]) [APap][mM]$/;
    const errorMessage =
      "Please enter a valid time (supported format 12 hours: h:m am/pm)";
    let error = false;
    timeValue.split(",").forEach((time) => {
      if (!timeValidationRegex.test(time.trim())) {
        error = true;
      }
    });
    if (error) {
      return errorData(errorMessage);
    }
  }
};

export const isValidAdFormatType = (adFormatsStr) => {
  const adFormats = split(toLower(adFormatsStr.replace(/,$/, "")), ",");
  let error = false;
  forEach(adFormats, (size) => {
    if (!size.includes("x") || split(size, "x").length > 2) {
      error = true;
    }
    const index = size.indexOf("x");
    if (
      !(parseInt(size.substr(0, index)) > 0) ||
      !(parseInt(size.substr(index + 1)) > 0)
    ) {
      error = true;
    }
  });
  if (error) {
    return errorData(
      "Please provide valid positive numbers for Width and Height in WxH format."
    );
  }
};

export const isValidEids = (eidsStr) => {
  const eids = splitCommaSeparatedStringToArray(eidsStr);
  let delimiter_errors = false;
  forEach(eids, (eid) => {
    if ((eid.match(/:/g) || []).length !== 1) {
      delimiter_errors = true;
    }
  });
  if (delimiter_errors) {
    return errorData(
      `Each EID provided should follow a format of source:uid.id (e.g. placeexchange.com:12345). One or more of the EIDs provided is incorrectly formatted`
    );
  }
};

export const isValidAspectRatioType = (formattedRecords) => {
  let ratioStr = get(formattedRecords, "value", "");
  const validRatios = ["16:9", "9:16", "4:3", "3:4"];
  const aspectRatios = ratioStr.split(",").map((value) => value.trim());
  let error = false;
  forEach(aspectRatios, (ratio) => {
    if (!includes(validRatios, ratio)) {
      error = true;
    }
  });
  if (error) {
    return errorData(
      `Please provide a valid supported ratio. Available Aspect Ratios are:${validRatios}`
    );
  }
};

export const isValidWeekdays = (weekdays) => {
  const validWeekDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const weekDayValues = commaSeparatedStringToArray(weekdays).map((value) =>
    value.trim()
  );
  let error = false;

  forEach(weekDayValues, (day) => {
    if (!includes(validWeekDays, day)) {
      error = true;
    }
  });
  if (error) {
    return errorData(
      `Please provide a valid weekday value. Available weekday values are:${validWeekDays}`
    );
  }
};

// Validate UUID format
export const uuidValidation = (uuid) => {
  const errorMessage = "Invalid UUID.";
  if (!validate(uuid)) {
    return errorData(errorMessage);
  }
};

export const validateSetcapFields = (recordRef) => {
  const dailyCap = recordRef["daily"];
  const lifetimeCap = recordRef["lifetime"];
  if (dailyCap && lifetimeCap) {
    if (Number(dailyCap) > Number(lifetimeCap)) {
      return errorData("Daily cap cannot exceed lifetime cap");
    }
  }
};

export const validateNetwork = async (
  inpNetworkId,
  inpNetworkName,
  orgId,
  mode
) => {
  if (mode === "change") {
    await getAvailableResources(orgId, {
      model: "Network",
      ids: [inpNetworkId],
      show_ids_and_names: "true",
    });
  }

  let returnMsg = "";
  if (
    serverResults &&
    !serverResults.map((item) => item.id).includes(inpNetworkId)
  ) {
    returnMsg = "Network ID does not belong to this organisation.";
  }
  if (serverResults && serverResults.includes("API Failed")) {
    returnMsg = ErrorMessages.API_FAILED_MESSAGE;
  }

  return returnMsg;
};

// Network ID with Network Name mismatch validation.
export const validateNetworkName = (inpNetworkId, inpNetworkName) => {
  if (serverResults && serverResults.includes("API Failed")) {
    return ErrorMessages.API_FAILED_MESSAGE;
  } else if (
    inpNetworkName &&
    inpNetworkId &&
    serverResults &&
    serverResults.find(
      ({ id, name }) => name !== inpNetworkName && id === inpNetworkId
    )
  ) {
    return "Network ID and Network Name mismatch.";
  }
};

let serverResults = [];
export const getAvailableResources = async (orgId, payload) =>
  fetchHandler({
    method: "POST",
    url: constructAdminAPIEndPoint({
      url: `orgs/${orgId}/resources-available`,
    }),
    headers: {
      "cache-control": "no-cache, no-store",
    },
    body: payload,
  })
    .then((json) => {
      if (payload.show_ids_and_names) {
        serverResults = json.resources_available_ids_and_names;
        return json.resources_available_ids_and_names;
      } else if (payload.show_ids) {
        return json.resources_available_ids;
      } else if (payload.show_names) {
        return json;
      } else if (payload.show_fields) {
        return json.resources_available;
      } else {
        return json.resources_unavailable_count !== 0;
      }
    })
    .catch(() => (serverResults = ["API Failed"]));

export const countLengthValidation = (type, value, limit) => {
  if (type === "textLength" && value.length > limit) {
    return errorData(`Maximum length of ${limit} exceeded`);
  }

  if (type === "countLength" && replace(value, ".", "").length > limit) {
    return errorData(
      `Ensure that there are no more than ${limit} digits in total.`
    );
  }

  if (
    type === "digitsBeforeDecimal" &&
    replace(value.split(".")[0], "-", "").length > limit
  ) {
    return errorData(
      `Ensure that there are no more than ${limit} digits before the decimal point.`
    );
  }

  if (type === "isGreaterThan" && Number(value) > Number(limit)) {
    return errorData(`Ensure this value is less than or equal to ${limit}.`);
  }
};

export const textRangeValidation = (value, minLength, maxlength) => {
  if (value.length < minLength || value.length > maxlength) {
    return errorData(
      `Character length should be between ${minLength} and ${maxlength}`
    );
  }
};

export const setVenueList = (list) => (venueList = list);

export const validateVenueOpenOOHCategory = (list) => {
  if (!isEmpty(list)) {
    let error = false,
      errorList = "",
      errorMsg = "";
    const venueOpenOOHList = venueList.map((i) => i.label);
    const updatedList = commaSeparatedStringToArray(list);
    forEach(updatedList, (item) => {
      if (!venueOpenOOHList.includes(item)) {
        error = true;
        errorList = !isEmpty(errorList) ? `${errorList}, ${item}` : `${item}`;
      }
    });
    if (error) {
      errorMsg =
        commaSeparatedStringToArray(errorList).length === 1
          ? `${errorList} is not valid venue_openooh category`
          : `${errorList} are not valid venue_openooh category`;
      return errorData(errorMsg);
    }
  }
};

export const setCountryList = (list) => (countryList = list);

const generateErrorMessage = (errorList, type, erroType) => {
  let messge = "";
  let single = "";
  erroType === "duplicate"
    ? (messge = "duplicte input given.")
    : (messge = "not valid choice.");
  commaSeparatedStringToArray(errorList).length === 1
    ? (single = "is")
    : (single = "are");
  let errorMsg = `${errorList} ${single} ${messge}`;
  type == "country" || erroType === "duplicate"
    ? (errorMsg = errorMsg)
    : (errorMsg =
        errorMsg + " (not belongs to the input targeting.geo.country list).");
  return errorMsg;
};

const validateUnique = (list) =>
  filter(list, (val, index, iteratee) => includes(iteratee, val, index + 1));

const validateChoice = (updatedList, type, validateData) => {
  let wrongChoice = [];
  let possibleList = [];
  if (type === "country") {
    possibleList = countryList;
  } else {
    let arrayList = validateData.map((data) => data[`${type}`]);
    if (type === "dma") {
      arrayList = arrayList.map((dmaData) => keys(dmaData));
    }
    possibleList = flatten(arrayList);
  }
  forEach(updatedList, (item) => {
    if (!possibleList.includes(item)) {
      wrongChoice.push(item);
    }
  });
  return wrongChoice;
};

const dataValidationGeo = (updatedList, type, validateData, errorList) => {
  let duplicates = validateUnique(updatedList);
  if (duplicates.length >= 1) {
    errorList = duplicates.join(", ");
    return errorData(generateErrorMessage(errorList, type, "duplicate"));
  }
  let wrongChoice = validateChoice(updatedList, type, validateData);
  if (wrongChoice.length >= 1) {
    errorList = wrongChoice.join(", ");
    return errorData(generateErrorMessage(errorList, type, "not valid input"));
  }
};

export const validateGeoData = (list, type, validateData) => {
  const fieldName = type.split(".");
  type = fieldName[fieldName.length - 1];
  if (!isEmpty(list) && !isEmpty(validateData)) {
    let errorList = "";
    let updatedList;
    type === "dma"
      ? (updatedList = splitCommaSeparatedStringToArray(list))
      : (updatedList = commaSeparatedStringToArray(list));
    return dataValidationGeo(updatedList, type, validateData, errorList);
  }
};

export const validateEntity = (entityNames, field, entityLengthCount = 35) => {
  let isNameValid = true,
    isMaxLengthExceed = false,
    maxLengthExceedKeywordList = [];
  const entityNameList = commaSeparatedStringToArray(entityNames);
  const entityNameRegex = /^[a-zA-Z0-9-_]+$/;
  forEach(entityNameList, (entity) => {
    if (!entityNameRegex.test(entity)) {
      isNameValid = false;
    }
    if (entity.length > entityLengthCount) {
      maxLengthExceedKeywordList.push(entity);
      isMaxLengthExceed = true;
    }
  });
  if (!isNameValid)
    return errorData(
      `${field} can accept these [a-zA-Z0-9_-] set of characters`
    );
  if (isMaxLengthExceed)
    return errorData(
      `${field} cannot contain more than 35 characters. please fix keyword(s) - ${maxLengthExceedKeywordList.join(
        ","
      )}`
    );
};
